<script setup lang="ts">
import { onMounted, onUnmounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

// Auto-hide the sticky top header on scroll-down, reveal on scroll-up.
// Header collapses via negative margin-top equal to its own height so
// content below fills the freed space (rather than leaving a gap).
const top = ref<HTMLElement | null>(null);
const body = ref<HTMLElement | null>(null);
const headerHidden = ref(false);
const topHeight = ref(0);
const keyboardOpen = ref(false);

const HIDE_AFTER = 80;
const DELTA_THRESHOLD = 8;
// Bigger threshold on the reveal direction so a tiny upward nudge doesn't
// snap the header back in — bidder has to commit to scrolling up.
const REVEAL_THRESHOLD = 200;

let lastScrollTop = 0;
let upAccumulator = 0;
let resizeObs: ResizeObserver | null = null;

const route = useRoute();
// Header is pinned by default. Routes opt in to the auto-hide behavior
// via `meta.autoHideHeader = true`. Currently catalog is the only one
// that benefits — it's a long scrolling grid; the rest of the bidder
// surfaces are short enough that hiding the header just feels jumpy.
const pinHeader = ref(true);
watch(
  () => route.meta.autoHideHeader === true,
  (autoHide) => {
    pinHeader.value = !autoHide;
    if (pinHeader.value) headerHidden.value = false;
  },
  { immediate: true },
);

function onScroll(): void {
  const el = body.value;
  if (!el) return;
  if (pinHeader.value) {
    // Header stays put on focused routes (register, sign-in). Track
    // scroll position so we don't snap when leaving the route, but
    // never set headerHidden true here.
    lastScrollTop = el.scrollTop;
    return;
  }
  const t = el.scrollTop;
  const delta = t - lastScrollTop;
  if (Math.abs(delta) < DELTA_THRESHOLD) return;
  if (t < HIDE_AFTER) {
    headerHidden.value = false;
    upAccumulator = 0;
  } else if (delta > 0) {
    headerHidden.value = true;
    upAccumulator = 0;
  } else {
    upAccumulator += -delta;
    if (upAccumulator >= REVEAL_THRESHOLD) {
      headerHidden.value = false;
    }
  }
  lastScrollTop = t;
}

function measureTop(): void {
  if (top.value) topHeight.value = top.value.getBoundingClientRect().height;
}

// Detect on-screen keyboard two ways and OR them — different browsers
// fire different signals.
//
// (1) Focus on a text-entry element. Reliable on every browser, fires
//     synchronously the moment the user taps a field, doesn't depend on
//     viewport math. We debounce focusout so tabbing between two inputs
//     doesn't flicker the nav.
// (2) visualViewport shrink. Catches edge cases where the keyboard
//     appears without focus moving (system autofill, accessory bar).
const KEYBOARD_THRESHOLD = 150;
const TEXT_ENTRY_TYPES = new Set([
  'text', 'email', 'password', 'tel', 'number', 'search', 'url',
  'date', 'datetime-local', 'month', 'time', 'week',
]);

function isTextEntry(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  if (el.isContentEditable) return true;
  if (el instanceof HTMLTextAreaElement) return true;
  if (el instanceof HTMLInputElement) {
    return TEXT_ENTRY_TYPES.has(el.type);
  }
  return false;
}

let focusOpen = false;
let viewportOpen = false;
let focusOutTimer: ReturnType<typeof setTimeout> | null = null;

function syncKeyboardOpen(): void {
  keyboardOpen.value = focusOpen || viewportOpen;
}

function onFocusIn(e: FocusEvent): void {
  if (!isTextEntry(e.target)) return;
  if (focusOutTimer) {
    clearTimeout(focusOutTimer);
    focusOutTimer = null;
  }
  focusOpen = true;
  syncKeyboardOpen();
}

function onFocusOut(): void {
  // Debounce — when the user taps from one input to the next, focusout
  // fires before focusin. A short delay collapses both events so the
  // nav doesn't flicker between fields.
  if (focusOutTimer) clearTimeout(focusOutTimer);
  focusOutTimer = setTimeout(() => {
    focusOpen = false;
    syncKeyboardOpen();
  }, 120);
}

function onViewportResize(): void {
  if (typeof window === 'undefined') return;
  const vv = window.visualViewport;
  if (!vv) return;
  viewportOpen = window.innerHeight - vv.height > KEYBOARD_THRESHOLD;
  syncKeyboardOpen();
}

onMounted(() => {
  measureTop();
  if (top.value && typeof ResizeObserver !== 'undefined') {
    resizeObs = new ResizeObserver(measureTop);
    resizeObs.observe(top.value);
  }
  body.value?.addEventListener('scroll', onScroll, { passive: true });
  if (typeof document !== 'undefined') {
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
  }
  if (typeof window !== 'undefined' && window.visualViewport) {
    window.visualViewport.addEventListener('resize', onViewportResize);
    onViewportResize();
  }
});
onUnmounted(() => {
  resizeObs?.disconnect();
  body.value?.removeEventListener('scroll', onScroll);
  if (typeof document !== 'undefined') {
    document.removeEventListener('focusin', onFocusIn);
    document.removeEventListener('focusout', onFocusOut);
  }
  if (typeof window !== 'undefined' && window.visualViewport) {
    window.visualViewport.removeEventListener('resize', onViewportResize);
  }
  if (focusOutTimer) clearTimeout(focusOutTimer);
});
</script>

<template>
  <div
    class="app-shell"
    data-testid="app-shell"
    :style="{ '--app-shell-top-height': topHeight + 'px' }"
  >
    <header
      ref="top"
      class="app-shell__top"
      :class="{ 'app-shell__top--hidden': headerHidden }"
    >
      <slot name="top" />
    </header>
    <div class="app-shell__middle">
      <aside class="app-shell__side">
        <slot name="side" />
      </aside>
      <main
        ref="body"
        class="app-shell__body"
        :class="{ 'app-shell__body--header-hidden': headerHidden }"
      >
        <div class="app-shell__well">
          <slot />
        </div>
      </main>
    </div>
    <footer
      class="app-shell__bottom"
      :class="{ 'app-shell__bottom--hidden': keyboardOpen }"
      :aria-hidden="keyboardOpen"
    >
      <slot name="bottom" />
    </footer>
  </div>
</template>

<style scoped>
.app-shell {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: var(--app-background-ghost, var(--app-background-primary));
  color: var(--app-text-primary);
}
.app-shell__top {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 10;
  background: var(--app-background-elevated);
  border-bottom: var(--app-border-width-default) solid var(--app-border-muted);
  transform: translateY(0);
  transition: transform 320ms cubic-bezier(0.4, 0, 0.2, 1);
  will-change: transform;
  /* Fixed header has no scroll content of its own. Without this, a touch
     drag starting here falls through to the document and pans/rubber-bands
     the whole page, visibly desyncing the in-flow sticky bottom nav from
     truly position:fixed overlays (e.g. the feedback FAB). pinch-zoom (not
     none) so accessibility pinch-to-zoom still works over the header.
     Scoped to just this element so the document itself stays
     scrollable/bounceable elsewhere (native pull-to-refresh still works
     from the top of .app-shell__body). */
  touch-action: pinch-zoom;
}
.app-shell__top--hidden {
  transform: translateY(-100%);
}
/* Middle row: at mobile the side rail is empty, so this just behaves
   like the body. At tablet+ the side rail takes a fixed-width column. */
.app-shell__middle {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: row;
  overflow: hidden;
}
.app-shell__side {
  display: none;
  background: var(--app-background-primary);
}
.app-shell__body {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  /* Deliberately left at the default (auto) overscroll behavior — this is
     the real scroll surface, so pulling past its top should chain to the
     document and stay pull-to-refresh eligible. The old `contain` here
     also blocked that chaining as a side effect while papering over the
     .app-shell__top drag bug, which is now fixed at its source. */
  background: var(--app-background-primary);
  /* Push content below the fixed header. When the header is hidden the
     margin animates to 0 so content fills the freed space in sync with
     the header slide-out. */
  margin-top: var(--app-shell-top-height, 56px);
  transition: margin-top 320ms cubic-bezier(0.4, 0, 0.2, 1);
  /* Container query context for the well — feature pages (catalog grid,
     item detail reflow) read against this so column counts reflect
     actual available width, not viewport (accurate when the sidebar
     occupies 240px on tablet+). */
  container-type: inline-size;
  container-name: app-shell-body;
}
.app-shell__body--header-hidden {
  margin-top: 0;
}
.app-shell__well {
  max-width: var(--app-well-max-width);
  margin: 0 auto;
  padding: var(--app-space-16) var(--app-space-20) var(--app-space-80);
}
.app-shell__bottom {
  position: sticky;
  bottom: 0;
  z-index: 10;
  background: var(--app-background-primary);
  padding-bottom: env(safe-area-inset-bottom);
  transition: transform 200ms ease, opacity 200ms ease;
}
.app-shell__bottom--hidden {
  /* Slide out + fade out when the soft keyboard is open so the nav
     doesn't float above the keyboard. visualViewport listener in the
     script flips the class based on innerHeight − visualViewport.height. */
  transform: translateY(110%);
  opacity: 0;
  pointer-events: none;
}

/* Tablet+ layout: left rail visible, bottom slot stays hidden (App.vue
   conditionally renders SideNav vs BottomNav by media query). At this
   width the content well already fits the viewport, so auto-hide
   header behavior is suppressed visually — JS keeps firing but CSS
   pins the header in place. */
@media (min-width: 641px) {
  .app-shell__side {
    display: block;
  }
  .app-shell__top--hidden {
    transform: translateY(0);
  }
  .app-shell__body--header-hidden {
    margin-top: var(--app-shell-top-height, 56px);
  }
}
</style>
